import { Reveal, MaskLines } from "./Reveal";
import { Pathway } from "./Pathway";
import { FeedbackLoop } from "./FeedbackLoop";
import { ArrowUpRight, Quote, Check } from "lucide-react";

const Shell = ({ children, className = "" }) => (
  <div className="flex h-full w-full items-center">
    <div className={`mx-auto w-full max-w-[1280px] px-8 py-14 md:px-16 md:py-16 ${className}`}>
      {children}
    </div>
  </div>
);

const Overline = ({ children, active, delay = 0 }) => (
  <Reveal active={active} delay={delay}>
    <div className="flex items-center gap-3">
      <span className="h-2 w-2 bg-canary" />
      <span className="overline text-stone">{children}</span>
    </div>
  </Reveal>
);

const StatBlock = ({ value, label, active, delay }) => (
  <Reveal active={active} delay={delay} className="border-t border-ink pt-3">
    <div data-testid="stat-block" className="font-display text-4xl leading-none tracking-tight md:text-6xl">
      {value}
    </div>
    <div className="mt-2 text-xs leading-snug text-stone md:text-sm">{label}</div>
  </Reveal>
);

const Marquee = ({ items }) => (
  <div className="relative overflow-hidden border-y border-ink py-3">
    <div className="flex w-max animate-marquee whitespace-nowrap">
      {[...items, ...items].map((t, i) => (
        <span key={i} className="mono mx-6 text-xs uppercase tracking-widest text-stone">
          {t} <span className="text-canary">✦</span>
        </span>
      ))}
    </div>
  </div>
);

/* ---------------- COVER ---------------- */
const Cover = ({ slide, images, active }) => (
  <div className="flex h-full w-full flex-col">
    <div className="flex flex-1 items-center">
      <div className="mx-auto grid w-full max-w-[1280px] items-center gap-10 px-8 md:grid-cols-12 md:px-16">
        <div className="md:col-span-7">
          <Overline active={active}>{slide.overline}</Overline>
          <h1 className="font-display mt-6 text-6xl font-black leading-[0.92] tracking-tight md:text-8xl">
            <MaskLines lines={slide.title.split("\n")} active={active} />
          </h1>
          <Reveal active={active} delay={0.55} className="mt-7 max-w-xl">
            <p className="text-base leading-relaxed text-stone md:text-lg">{slide.subtitle}</p>
          </Reveal>
          <Reveal active={active} delay={0.7} className="mt-8">
            <span className="mono text-xs uppercase tracking-widest text-ink">{slide.footnote}</span>
          </Reveal>
        </div>
        <div className="md:col-span-5">
          <Reveal active={active} delay={0.4}>
            <div className="relative">
              <div className="absolute -left-4 -top-4 h-full w-full border border-ink" />
              <div className="absolute right-4 top-4 z-0 h-24 w-24 bg-canary" />
              <div className="relative z-10 aspect-[4/5] overflow-hidden border border-ink">
                <img
                  src={images.canary}
                  alt="A canary — the early signal"
                  className="h-full w-full object-cover grayscale transition-all duration-700 hover:grayscale-0 print-grayscale"
                />
              </div>
              <div className="relative z-10 -mt-px border border-t-0 border-ink bg-ink px-4 py-2">
                <span className="mono text-[0.65rem] uppercase tracking-widest text-canary">
                  The canary signals first
                </span>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
    <div className="no-print pb-24 md:pb-28">
      <Marquee items={["Community listening", "Original reporting", "Accessible distribution", "Convening", "Accountability & action"]} />
    </div>
  </div>
);

/* ---------------- PROBLEM ---------------- */
const Problem = ({ slide, images, active }) => (
  <Shell>
    <div className="grid gap-10 md:grid-cols-12">
      <div className="md:col-span-5">
        <Overline active={active}>{slide.overline}</Overline>
        <h2 className="font-display mt-5 text-4xl leading-[0.98] tracking-tight md:text-6xl">
          {slide.title}
        </h2>
        <Reveal active={active} delay={0.25} className="mt-6 max-w-md">
          <p className="text-sm leading-relaxed text-stone md:text-base">{slide.subtitle}</p>
        </Reveal>
        <Reveal active={active} delay={0.4} className="mt-8 hidden md:block">
          <div className="aspect-[16/10] overflow-hidden border border-ink">
            <img src={images.newspapers} alt="Decline of local print news" className="h-full w-full object-cover grayscale print-grayscale" />
          </div>
        </Reveal>
      </div>
      <div className="md:col-span-7">
        <div className="grid gap-0">
          {slide.items.map((it, i) => (
            <Reveal key={i} active={active} delay={0.3 + i * 0.14}>
              <div className="flex gap-6 border-t border-ink py-6">
                <span className="font-display text-3xl leading-none text-canary md:text-4xl" style={{ WebkitTextStroke: "1px #111" }}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3 className="font-display text-2xl tracking-tight md:text-3xl">{it.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-stone md:text-base">{it.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </Shell>
);

/* ---------------- INSIGHT ---------------- */
const Insight = ({ slide, images, active }) => (
  <Shell>
    <div className="grid items-center gap-10 md:grid-cols-12">
      <div className="md:col-span-5">
        <Reveal active={active} delay={0.35}>
          <div className="relative">
            <div className="absolute right-3 top-3 h-full w-full bg-canary" />
            <div className="relative aspect-[4/5] overflow-hidden border border-ink">
              <img src={images.insight || images.summer || images.founder} alt="Community members at a Canary Report convening" className="h-full w-full object-cover grayscale transition-all duration-700 hover:grayscale-0 print-grayscale" />
            </div>
          </div>
        </Reveal>
      </div>
      <div className="md:col-span-7">
        <Overline active={active}>{slide.overline}</Overline>
        <Reveal active={active} delay={0.2}>
          <Quote className="mt-5 h-8 w-8 text-canary" />
        </Reveal>
        <h2 className="font-display mt-3 text-4xl leading-[1.02] tracking-tight md:text-5xl">
          {slide.title}
        </h2>
        <Reveal active={active} delay={0.35} className="mt-6 max-w-xl">
          <p className="text-base leading-relaxed text-stone md:text-lg">{slide.subtitle}</p>
        </Reveal>
        <Reveal active={active} delay={0.55} className="mt-8">
          <div className="inline-block border-l-2 border-canary bg-ink px-5 py-3">
            <span className="mono text-xs uppercase tracking-widest text-paper">{slide.footnote}</span>
          </div>
        </Reveal>
      </div>
    </div>
  </Shell>
);

/* ---------------- SOLUTION ---------------- */
const Solution = ({ slide, active }) => (
  <Shell>
    <Overline active={active}>{slide.overline}</Overline>
    <div className="mt-5 flex flex-col justify-between gap-4 md:flex-row md:items-end">
      <h2 className="font-display max-w-lg text-4xl leading-[0.98] tracking-tight md:text-6xl">{slide.title}</h2>
      <Reveal active={active} delay={0.2} className="max-w-sm">
        <p className="text-sm leading-relaxed text-stone md:text-base">{slide.subtitle}</p>
      </Reveal>
    </div>
    <div className="mt-10">
      <FeedbackLoop items={slide.items} active={active} />
    </div>
  </Shell>
);

/* ---------------- PRODUCTS ---------------- */
const Products = ({ slide, active }) => (
  <Shell>
    <Overline active={active}>{slide.overline}</Overline>
    <div className="mt-5 flex flex-col justify-between gap-4 md:flex-row md:items-end">
      <h2 className="font-display max-w-lg text-4xl leading-[0.98] tracking-tight md:text-6xl">{slide.title}</h2>
      <Reveal active={active} delay={0.2} className="max-w-sm">
        <p className="text-sm leading-relaxed text-stone md:text-base">{slide.subtitle}</p>
      </Reveal>
    </div>
    <div className="mt-10 grid gap-px border border-ink bg-ink md:grid-cols-3">
      {slide.items.map((it, i) => (
        <Reveal key={i} active={active} delay={0.2 + i * 0.07}>
          <div className="group h-full bg-surface p-5 transition-colors duration-300 hover:bg-canary">
            <span className="mono text-xs text-stone">{String(i + 1).padStart(2, "0")}</span>
            <h3 className="font-display mt-3 text-lg leading-tight tracking-tight md:text-xl">{it.title}</h3>
            <p className="mt-1.5 text-[0.8rem] leading-relaxed text-stone group-hover:text-ink">{it.desc}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </Shell>
);

/* ---------------- PATHWAY ---------------- */
const PathwaySlide = ({ slide, active }) => (
  <Shell>
    <div className="grid gap-6 md:grid-cols-12 md:items-end">
      <div className="md:col-span-7">
        <Overline active={active}>{slide.overline}</Overline>
        <h2 className="font-display mt-5 text-4xl leading-[0.98] tracking-tight md:text-6xl">{slide.title}</h2>
      </div>
      <Reveal active={active} delay={0.2} className="md:col-span-5">
        <p className="text-sm leading-relaxed text-stone md:text-base">{slide.subtitle}</p>
      </Reveal>
    </div>
    <div className="mt-12">
      <Pathway items={slide.items} active={active} />
    </div>
  </Shell>
);

/* ---------------- TRACTION ---------------- */
const Traction = ({ slide, active }) => (
  <Shell>
    <Overline active={active}>{slide.overline}</Overline>
    <div className="mt-5 flex flex-col justify-between gap-4 md:flex-row md:items-end">
      <h2 className="font-display text-4xl leading-[0.98] tracking-tight md:text-6xl">{slide.title}</h2>
      <Reveal active={active} delay={0.2} className="max-w-sm">
        <p className="text-sm leading-relaxed text-stone md:text-base">{slide.subtitle}</p>
      </Reveal>
    </div>
    <div className="mt-8 grid gap-8 md:grid-cols-4">
      {slide.stats.map((s, i) => (
        <StatBlock key={i} {...s} active={active} delay={0.2 + i * 0.1} />
      ))}
    </div>
    <div className="mt-8 grid gap-4 md:grid-cols-2">
      {slide.items.map((it, i) => (
        <Reveal key={i} active={active} delay={0.5 + i * 0.12}>
          <div className="flex h-full gap-3 border border-ink bg-surface p-4">
            <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-canary" style={{ strokeWidth: 3 }} />
            <div>
              <h3 className="font-display text-lg tracking-tight">{it.title}</h3>
              <p className="mt-1 text-sm leading-relaxed text-stone">{it.desc}</p>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
    <Reveal active={active} delay={0.8} className="mt-8">
      <p className="overline mb-3 text-stone">Partners</p>
      <div className="flex flex-wrap gap-2">
        {slide.partners.map((p, i) => (
          <span key={i} className="border border-ink px-3 py-1.5 text-xs font-medium">{p}</span>
        ))}
      </div>
    </Reveal>
  </Shell>
);

/* ---------------- BUSINESS ---------------- */
const Business = ({ slide, active }) => (
  <Shell>
    <Overline active={active}>{slide.overline}</Overline>
    <div className="mt-5 flex flex-col justify-between gap-4 md:flex-row md:items-end">
      <h2 className="font-display max-w-lg text-4xl leading-[0.98] tracking-tight md:text-6xl">{slide.title}</h2>
      <Reveal active={active} delay={0.2} className="max-w-sm">
        <p className="text-sm leading-relaxed text-stone md:text-base">{slide.subtitle}</p>
      </Reveal>
    </div>
    <div className="mt-10 grid gap-px border border-ink bg-ink md:grid-cols-3">
      {(slide.groups || []).map((g, i) => (
        <Reveal key={i} active={active} delay={0.2 + i * 0.12}>
          <div className="flex h-full flex-col bg-surface p-6">
            <div className="flex items-center gap-2 border-b border-ink pb-3">
              <span className="h-2 w-2 flex-shrink-0 bg-canary" />
              <span className="mono text-xs uppercase tracking-widest">{g.label}</span>
            </div>
            <ul className="mt-4 space-y-3">
              {g.items.map((it, j) => (
                <li key={j} className="font-display text-lg leading-tight tracking-tight md:text-xl">{it}</li>
              ))}
            </ul>
          </div>
        </Reveal>
      ))}
    </div>
  </Shell>
);

/* ---------------- MARKET ---------------- */
const Market = ({ slide, active }) => (
  <Shell>
    <div className="grid gap-10 md:grid-cols-12">
      <div className="md:col-span-5">
        <Overline active={active}>{slide.overline}</Overline>
        <h2 className="font-display mt-5 text-4xl leading-[0.98] tracking-tight md:text-5xl">{slide.title}</h2>
        <Reveal active={active} delay={0.25} className="mt-6 max-w-md">
          <p className="text-sm leading-relaxed text-stone md:text-base">{slide.subtitle}</p>
        </Reveal>
        <div className="mt-8 grid grid-cols-2 gap-6">
          {slide.stats.map((s, i) => (
            <StatBlock key={i} {...s} active={active} delay={0.4 + i * 0.12} />
          ))}
        </div>
      </div>
      <div className="md:col-span-7 md:border-l md:border-ink md:pl-10">
        <div className="grid gap-0">
          {slide.items.map((it, i) => (
            <Reveal key={i} active={active} delay={0.3 + i * 0.12}>
              <div className="flex items-baseline gap-5 border-b border-ink py-5">
                <span className="mono text-xs text-canary" style={{ WebkitTextStroke: "0.4px #111" }}>{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <h3 className="font-display text-2xl tracking-tight md:text-3xl">{it.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-stone">{it.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
    {slide.footnote && (
      <Reveal active={active} delay={0.7} className="mt-8 border-t border-ink pt-4">
        <p className="text-sm leading-relaxed text-stone md:text-base">
          <span className="mono uppercase tracking-widest text-ink">Stakeholders — </span>
          {slide.footnote}
        </p>
      </Reveal>
    )}
  </Shell>
);

/* ---------------- COOPERATIVE ---------------- */
const Cooperative = ({ slide, active }) => (
  <Shell>
    <div className="max-w-3xl">
      <Overline active={active}>{slide.overline}</Overline>
      <h2 className="font-display mt-5 text-4xl leading-[1.0] tracking-tight md:text-6xl">{slide.title}</h2>
      <Reveal active={active} delay={0.25} className="mt-6">
        <p className="text-base leading-relaxed text-stone md:text-lg">{slide.subtitle}</p>
      </Reveal>
    </div>
    <div className="mt-12 grid gap-4 md:grid-cols-3">
      {slide.items.map((it, i) => (
        <Reveal key={i} active={active} delay={0.35 + i * 0.15}>
          <div className="h-full border-t-2 border-canary bg-surface p-6">
            <span className="mono text-xs text-stone">0{i + 1}</span>
            <h3 className="font-display mt-2 text-2xl tracking-tight">{it.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-stone">{it.desc}</p>
          </div>
        </Reveal>
      ))}
    </div>
    {slide.footnote && (
      <Reveal active={active} delay={0.85} className="mt-8 border-t border-ink pt-4">
        <p className="text-sm leading-relaxed text-stone md:text-base">{slide.footnote}</p>
      </Reveal>
    )}
  </Shell>
);

/* ---------------- TEAM ---------------- */
const Team = ({ slide, images, active }) => (
  <Shell>
    <div className="grid items-center gap-10 md:grid-cols-12">
      <div className="md:col-span-5">
        <Reveal active={active} delay={0.3}>
          <div className="relative">
            <div className="absolute -bottom-4 -left-4 h-full w-full border border-ink" />
            <div className="relative aspect-[4/5] overflow-hidden border border-ink">
              <img src={images.summer || images.founder} alt="Summer D. Nettles, Founder" className="h-full w-full object-cover object-top grayscale transition-all duration-700 hover:grayscale-0 print-grayscale" />
            </div>
          </div>
        </Reveal>
      </div>
      <div className="md:col-span-7">
        <Overline active={active}>{slide.overline}</Overline>
        <h2 className="font-display mt-4 text-4xl leading-[1.0] tracking-tight md:text-6xl">{slide.title}</h2>
        <Reveal active={active} delay={0.25} className="mt-5 max-w-xl">
          <p className="text-base leading-relaxed text-stone md:text-lg">{slide.subtitle}</p>
        </Reveal>
        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          {slide.items.map((it, i) => (
            <Reveal key={i} active={active} delay={0.45 + i * 0.1}>
              <div className="border-l-2 border-ink pl-4">
                <h3 className="font-display text-lg tracking-tight">{it.title}</h3>
                <p className="text-sm text-stone">{it.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        {slide.footnote && (
          <Reveal active={active} delay={0.85} className="mt-6 border-t border-ink pt-4">
            <p className="text-sm leading-relaxed text-stone md:text-base">{slide.footnote}</p>
          </Reveal>
        )}
      </div>
    </div>
  </Shell>
);

/* ---------------- VISION ---------------- */
const Vision = ({ slide, active }) => (
  <Shell>
    <div className="max-w-4xl">
      <Overline active={active}>{slide.overline}</Overline>
      <h2 className="font-display mt-6 text-5xl leading-[1.0] tracking-tight md:text-7xl">{slide.title}</h2>
      <Reveal active={active} delay={0.3} className="mt-7 max-w-2xl">
        <p className="text-base leading-relaxed text-stone md:text-xl">{slide.subtitle}</p>
      </Reveal>
    </div>
    <div className="mt-12 grid gap-8 md:grid-cols-3">
      {slide.stats.map((s, i) => (
        <StatBlock key={i} {...s} active={active} delay={0.5 + i * 0.14} />
      ))}
    </div>
  </Shell>
);

/* ---------------- ASK / CLOSING ---------------- */
const Ask = ({ slide, images, active }) => (
  <div className="flex h-full w-full items-center bg-ink text-paper">
    <div className="mx-auto grid w-full max-w-[1280px] items-center gap-10 px-8 py-16 md:grid-cols-12 md:px-16">
      <div className="md:col-span-8">
        <Reveal active={active}>
          <div className="flex items-center gap-3">
            <span className="h-2 w-2 bg-canary" />
            <span className="overline text-canary">{slide.overline}</span>
          </div>
        </Reveal>
        <h2 className="font-display mt-6 text-5xl leading-[0.98] tracking-tight md:text-8xl">
          <MaskLines lines={slide.title.split(" ").reduce((acc, w, i) => {
            const li = Math.floor(i / 2);
            acc[li] = (acc[li] ? acc[li] + " " : "") + w;
            return acc;
          }, [])} active={active} />
        </h2>
        <Reveal active={active} delay={0.5} className="mt-7 max-w-xl">
          <p className="text-base leading-relaxed text-paper/70 md:text-lg">{slide.subtitle}</p>
        </Reveal>
        {slide.partner && (
          <Reveal active={active} delay={0.6} className="mt-7 border-t border-paper/20 pt-5">
            <div className="font-display text-2xl tracking-tight text-paper md:text-3xl">{slide.partner}</div>
            <div className="mono mt-1.5 text-xs uppercase tracking-widest text-canary">{slide.partnerDesc}</div>
          </Reveal>
        )}
        <Reveal active={active} delay={0.7} className="mt-8">
          <a
            href={`mailto:${(slide.footnote || "").split("·")[0].trim()}`}
            data-testid="ask-contact-link"
            className="group inline-flex items-center gap-3 bg-canary px-6 py-3 text-ink transition-transform duration-300 hover:-translate-y-0.5"
          >
            <span className="mono text-sm uppercase tracking-widest">{slide.footnote}</span>
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </Reveal>
      </div>
      <div className="md:col-span-4">
        <Reveal active={active} delay={0.4}>
          <div className="relative aspect-square overflow-hidden border border-canary">
            <img src={images.canary} alt="The Canary Report" className="h-full w-full object-cover grayscale print-grayscale" />
            <div className="absolute inset-0 bg-canary/10 mix-blend-multiply" />
          </div>
        </Reveal>
      </div>
    </div>
  </div>
);

export const SLIDE_TEMPLATES = {
  cover: Cover,
  problem: Problem,
  insight: Insight,
  solution: Solution,
  products: Products,
  pathway: PathwaySlide,
  traction: Traction,
  business: Business,
  market: Market,
  cooperative: Cooperative,
  team: Team,
  vision: Vision,
  ask: Ask,
};

export const SlideRenderer = ({ slide, images, active }) => {
  const Tpl = SLIDE_TEMPLATES[slide.type] || Problem;
  return <Tpl slide={slide} images={images} active={active} />;
};
